"use client";

import { memo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "@/lib/i18n";
import { TRUSTED_BY_STATS, pickLabel } from "@/lib/network-metrics";

/* ── Client list ── */
const CLIENTS = [
  { name: "A1", full: "A1 Bulgaria", style: "font-black tracking-wider uppercase" },
  { name: "CETIN", full: "CETIN Bulgaria", style: "font-black tracking-wider uppercase" },
  { name: "Cogent", full: "Cogent Communications", style: "font-bold tracking-tight" },
  { name: "Colt", full: "Colt Technology Services", style: "font-black tracking-wider uppercase" },
  { name: "EXA", full: "EXA Infrastructure", style: "font-black tracking-wider uppercase" },
  { name: "Hurricane Electric", full: "Hurricane Electric", style: "font-bold tracking-tight" },
  { name: "Telekom Slovenije", full: "Telekom Slovenije", style: "font-semibold" },
  { name: "Zayo", full: "Zayo Europe", style: "font-black tracking-wider" },
  { name: "iQ Networks", full: "iQ Networks / iQ Group", style: "font-semibold" },
  { name: "Caucasus Online", full: "Caucasus Online", style: "font-bold" },
  { name: "TEAM Telecom", full: "TEAM Telecom Armenia", style: "font-bold uppercase tracking-wide" },
  { name: "Ucom", full: "Ucom", style: "font-black tracking-wider" },
  { name: "Supercell Network", full: "Supercell Network", style: "font-semibold" },
  { name: "SkyTel", full: "SkyTel Georgia", style: "font-bold tracking-wide" },
  { name: "HGC", full: "HGC Global Communications", style: "font-black tracking-wider uppercase" },
];

/* Double the list for seamless infinite scroll */
const DOUBLED = [...CLIENTS, ...CLIENTS];

export const TrustedBy = memo(function TrustedBy() {
  const { t, language } = useTranslation();

  return (
    <section id="clients" className="py-20 px-4 sm:px-6 lg:px-8 bg-background relative overflow-hidden">
      {/* Subtle top/bottom border */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      <div className="mx-auto max-w-7xl relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          {/* Signal rebrand: lowercase mono eyebrow + Unbounded h2. */}
          <span
            className="font-mono text-[11px] sm:text-[12px] lowercase text-eyebrow"
            style={{ letterSpacing: "0.08em" }}
          >
            {t('trusted_eyebrow')}
          </span>
          <h2
            className="font-display mt-3 text-3xl sm:text-4xl tracking-tight text-foreground"
            style={{ fontWeight: 600 }}
          >
            {t('trusted_heading')}
          </h2>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
            {t('trusted_subtitle')}
          </p>
        </motion.div>

        {/* Logo carousel — infinite scroll */}
        <div className="relative">
          {/* Fade edges */}
          <div className="absolute left-0 top-0 bottom-0 w-20 sm:w-32 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 sm:w-32 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />

          {/* Scrolling track */}
          <div className="overflow-hidden">
            <motion.div
              className="flex items-center gap-12 sm:gap-16 w-max"
              animate={{ x: ["0%", "-50%"] }}
              transition={{
                x: {
                  repeat: Infinity,
                  repeatType: "loop",
                  duration: 40,
                  ease: "linear",
                },
              }}
            >
              {DOUBLED.map((client, i) => (
                <div
                  key={`${client.name}-${i}`}
                  className="flex-shrink-0 group"
                  title={client.full}
                >
                  <span
                    className={`text-xl sm:text-2xl text-muted-foreground/70 group-hover:text-muted-foreground transition-colors duration-300 whitespace-nowrap select-none ${client.style}`}
                  >
                    {client.name}
                  </span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8"
        >
          {TRUSTED_BY_STATS.map((stat) => {
            const label = pickLabel(stat.label, language);
            return (
              <div key={label} className="text-center">
                <div className="text-2xl sm:text-3xl font-bold font-mono text-foreground">
                  {stat.value}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{label}</div>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
});
TrustedBy.displayName = "TrustedBy";
